import { Decimal } from 'decimal.js';
import { round, merge } from 'lodash';

import {
  DEFAULT_CURRENCY_SYMBOL,
  DEFAULT_THOUSANDS_DIVIDER,
  DEFAULT_NUMBER_PRECISION,
  DEFAULT_DECIMAL_DIVIDER,
  DEFAULT_DISPLAY_DECIMAL,
} from '@config/global-constants';

import { commonCurrencyFormat } from './currency.format';
import {
  ICurrencyFormatOptions,
  ICurrencyOptions,
  ICurrencyParseOptions,
  ICurrency,
} from './currency.types';

export class Currency implements ICurrency {
  protected defaultOptions: ICurrencyOptions = {
    format: commonCurrencyFormat,
    defaultFormatValue: DEFAULT_DISPLAY_DECIMAL,
    isAddTrailZeros: true,
    symbol: DEFAULT_CURRENCY_SYMBOL,
    decimalSeparator: DEFAULT_DECIMAL_DIVIDER,
    thousandsSeparator: DEFAULT_THOUSANDS_DIVIDER,
    precision: DEFAULT_NUMBER_PRECISION,
    pattern: '!#',
    negativePattern: '-!#',
  };

  protected groupRegex: RegExp = /(\d)(?=(\d{3})+\b)/g;
  protected vedicRegex: RegExp = /(\d)(?=(\d\d)+\d\b)/g;

  public readonly currencyOpts: ICurrencyOptions;
  public readonly currencyFormatOpts: ICurrencyFormatOptions;

  public readonly value: number;
  public readonly valueDecimal: Decimal;

  public constructor(value: number | string, opts?: ICurrencyOptions) {
    this.currencyOpts = merge({}, this.defaultOptions, opts);
    this.currencyFormatOpts = merge({}, this.currencyOpts, {
      groups: this.currencyOpts.useVedic ? this.vedicRegex : this.groupRegex,
    });

    this.value = this.parse(value);
    this.valueDecimal = new Decimal(this.value);
  }

  public parse(
    value: number | string = this.value,
    opts: ICurrencyParseOptions = this.currencyOpts,
  ): number {
    // use rounding as default
    const { isRounding = true, ...restOpts } = opts;

    const numericValue = Number(value);

    return isRounding ? round(numericValue, restOpts.precision) : numericValue;
  }

  public format(opts?: Partial<ICurrencyFormatOptions>): string {
    const formatSettings = merge({}, this.currencyFormatOpts, opts);

    return formatSettings.format(this, formatSettings);
  }

  public toString(decimalPlaces?: number): string {
    return this.valueDecimal.toFixed(decimalPlaces);
  }
}
